import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import { useAuth, type LinkableProvider } from '../lib/auth';
import { initialsFor } from '../lib/types';

/** The friendliest reading of a Firebase failure, since every one must end in a message. */
function linkError(e: unknown, provider: string): string | null {
  const code = (e as { code?: string }).code ?? '';
  switch (code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return null;
    // The identity is already its own account — typically someone who signed up with
    // Google on the phone and with a password here. Merging two accounts means moving
    // notes between uids, which no client can do; it needs a function.
    case 'auth/credential-already-in-use':
    case 'auth/email-already-in-use':
      return `That ${provider} login already belongs to a separate ZsoogiNotes account, so it cannot be connected to this one.`;
    case 'auth/provider-already-linked':
      return `${provider} is already connected.`;
    case 'auth/weak-password':
      return 'That password is too short — use at least 6 characters.';
    case 'auth/requires-recent-login':
      return 'For your security, sign out and back in, then try again.';
    default:
      return `${(e as Error).message}${code ? ` (${code})` : ''}`;
  }
}

export default function Account() {
  const { user, profile, signOut, linkProvider, unlinkProvider, addPassword, sendPasswordReset } =
    useAuth();
  const navigate = useNavigate();
  // Local, because linking changes `auth.currentUser` without an auth-state event.
  const [providers, setProviders] = useState<string[]>(
    () => user?.providerData.map((p) => p.providerId) ?? []
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [addingPassword, setAddingPassword] = useState(false);

  const has = (id: string) => providers.includes(id);
  const emailFor = (id: string) => user?.providerData.find((p) => p.providerId === id)?.email;
  const onlyOne = providers.length < 2;

  async function attempt(label: string, fn: () => Promise<string[] | void>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await fn();
      if (next) setProviders(next);
    } catch (e) {
      setError(linkError(e, label));
    } finally {
      setBusy(false);
    }
  }

  const oauthRows: { id: LinkableProvider; name: string }[] = [
    { id: 'google.com', name: 'Google' },
    { id: 'apple.com', name: 'Apple' },
  ];

  const name = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ');

  return (
    <>
      <AppHeader title="Account" />

      <main>
        <section className="group">
          <h2 className="field-label">Profile</h2>
          <Link className="row link" to="/profile">
            <span className="avatar">{initialsFor(profile, user?.displayName, user?.email)}</span>
            <span className="row-main">
              <span>{name || user?.displayName || 'Add your name'}</span>
              <span className="muted small">{user?.email}</span>
            </span>
            <span className="muted" aria-hidden="true">›</span>
          </Link>
        </section>

        <section className="group">
          <h2 className="field-label">Subscription</h2>
          {/* Naming the plan is safe here, where it once was not. The web had no way to read
              entitlement — it comes from the RevenueCat SDK on the device — so this said
              nothing rather than risk showing "Free" to a subscriber. The `getEntitlement`
              callable now answers server-side, and reaching this screen at all means the
              answer was Pro: App.tsx renders the upgrade screen otherwise. Billing itself is
              still the phone's, because that is where the purchase lives. */}
          <div className="row">
            <span className="row-main">
              <span>Pro</span>
              <span className="muted small">
                The web app is a Pro feature. Billing is managed in the iOS app.
              </span>
            </span>
          </div>
        </section>

        <section className="group">
          <h2 className="field-label">Sign-in methods</h2>
          <p className="muted small">
            Connect more than one and use whichever is handy — on the web, iOS and Android
            alike. They all open the same notes.
          </p>

          <div className="row">
            <span className="row-main">
              <span>Email and password</span>
              <span className="muted small">{user?.email ?? '—'}</span>
            </span>
            {has('password') ? (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  attempt('Password', async () => {
                    await sendPasswordReset();
                    setNotice(`A link to change your password is on its way to ${user?.email}.`);
                  })
                }
              >
                Change
              </button>
            ) : (
              !addingPassword && (
                <button type="button" disabled={busy || !user?.email} onClick={() => setAddingPassword(true)}>
                  Add password
                </button>
              )
            )}
          </div>
          {addingPassword && !has('password') && (
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
                void attempt('Password', async () => {
                  const next = await addPassword(newPassword);
                  setNewPassword('');
                  setAddingPassword(false);
                  return next;
                });
              }}
            >
              <input
                type="password"
                placeholder="New password"
                autoComplete="new-password"
                minLength={6}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <button type="submit" disabled={busy}>Save</button>
              <button type="button" disabled={busy} onClick={() => setAddingPassword(false)}>
                Cancel
              </button>
            </form>
          )}

          {oauthRows.map(({ id, name }) => (
            <div className="row" key={id}>
              <span className="row-main">
                <span>{name}</span>
                <span className="muted small">
                  {has(id) ? emailFor(id) || 'Connected' : 'Not connected'}
                </span>
              </span>
              {has(id) ? (
                <button
                  type="button"
                  className="danger"
                  disabled={busy || onlyOne}
                  title={onlyOne ? 'Your only sign-in method cannot be removed.' : undefined}
                  onClick={() => attempt(name, () => unlinkProvider(id))}
                >
                  Disconnect
                </button>
              ) : (
                <button type="button" disabled={busy} onClick={() => attempt(name, () => linkProvider(id))}>
                  Connect
                </button>
              )}
            </div>
          ))}

          {error && <p className="error">{error}</p>}
          {notice && <p className="notice">{notice}</p>}
        </section>

        <section className="group">
          <h2 className="field-label">App information</h2>
          <div className="row">
            <span className="row-main"><span>Version</span></span>
            <span className="muted">{__APP_VERSION__}</span>
          </div>
          <div className="row">
            <span className="row-main"><span>Capture</span></span>
            <span className="muted">iOS and Android</span>
          </div>
        </section>

        <div className="actions">
          <button
            type="button"
            className="danger"
            onClick={() => {
              navigate('/');
              void signOut();
            }}
          >
            Sign out
          </button>
        </div>
      </main>
    </>
  );
}
